import React from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import cloneDeep from "lodash/cloneDeep";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  TextField,
} from "@mui/material";
import {
  DAY91_BILLING_CODE_OPTIONS,
  getCustomBillingCodes,
  getDay31BillingCodes,
} from "static/constants";
import useBillingSchedule from "./useBillingSchedule";
import { useSelector } from "react-redux";
import { getNumberInputValue } from "utils/number";
import { getMappedBillingCode } from "utils/billingCode.util";

// Define validation schema using Yup
const validationSchema = (billingSchedule) =>
  Yup.object({
    day31BillingCode: billingSchedule?.day31
      ? Yup.string().required("Day 31 Billing Code is required")
      : Yup.string(),
    day91BillingCode: billingSchedule?.day91
      ? Yup.string().required("Day 91 Billing Code is required")
      : Yup.string(),
    customDays: billingSchedule?.customEnabled
      ? Yup.number()
          .min(1, "Must be more than 0")
          .required("Custom Days is required when Custom Billing is enabled")
      : Yup.number(),
    customBillingCode: billingSchedule?.customEnabled
      ? Yup.string().required("Custom Billing Code is required")
      : Yup.string(),
  });

const BillingScheduleForm = ({
  isLoading,
  handleSubmit,
  isEditMode,
  setIsEditMode,
}) => {
  const { originalBillingSchedule } = useSelector(
    (state) => state.transmission
  );

  const {
    isIDTFBilling,
    newBillingSchedule,
    handleInputChange,

    handleBillingCycleCheckbox,
    handleCustomBillingCodeSelect,
  } = useBillingSchedule();

  return (
    <Formik
      initialValues={{
        day31: newBillingSchedule?.day31,
        day91: newBillingSchedule?.day91,
        customEnabled: newBillingSchedule?.custom?.enabled,
        customDays: newBillingSchedule?.custom?.days || "",
        day31BillingCode: newBillingSchedule?.day31BillingCode || "",
        day91BillingCode: newBillingSchedule?.day91BillingCode || "",
        customBillingCode: newBillingSchedule?.custom?.customBillingCode || "",
      }}
      validationSchema={validationSchema(newBillingSchedule)}
      onSubmit={(values) => handleSubmit()}
      enableReinitialize
    >
      {({ values, setFieldValue, errors, touched }) => (
        <Form>
          <Grid container justifyContent="space-between" spacing={1}>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newBillingSchedule?.day31}
                    onChange={(e) => {
                      setFieldValue("day31", e.target.checked);
                      handleBillingCycleCheckbox(true);
                    }}
                    name="day31"
                  />
                }
                label="31 day"
              />
            </Grid>

            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newBillingSchedule?.day91}
                    onChange={(e) => {
                      setFieldValue("day91", e.target.checked);
                      handleBillingCycleCheckbox();
                    }}
                    name="day91"
                  />
                }
                label="91 day"
              />
            </Grid>

            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.customEnabled}
                    onChange={(e) => {
                      const copy = cloneDeep(newBillingSchedule);
                      copy.custom.enabled = !newBillingSchedule.custom.enabled;
                      copy.custom.days = newBillingSchedule.custom.days;

                      handleInputChange(copy, originalBillingSchedule);
                      setFieldValue("customEnabled", e.target.checked);
                    }}
                    name="customEnabled"
                  />
                }
                label="Custom"
              />
            </Grid>
          </Grid>

          <Grid container justifyContent="space-between" spacing={1}>
            <Grid item xs={4}>
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel id="day31-label">31 day Billing Code</InputLabel>
                <Select
                  id="active"
                  value={getMappedBillingCode({
                    billingCode: newBillingSchedule.day31BillingCode,
                    isIDTFBilling,
                  })}
                  name="day31BillingCode"
                  label="31 day Billing Code"
                  onChange={(event) => {
                    const value = event.target.value;
                    const copy = cloneDeep(newBillingSchedule);
                    copy.day31BillingCode = value;

                    handleInputChange(copy, originalBillingSchedule);
                    setFieldValue("day31BillingCode", value);
                  }}
                  disabled={!newBillingSchedule?.day31}
                  error={
                    touched.day31BillingCode && Boolean(errors.day31BillingCode)
                  }
                >
                  {getDay31BillingCodes(isIDTFBilling).map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </Select>
                <ErrorMessage name="day31BillingCode" component="div" />
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                error={
                  touched.day91BillingCode && Boolean(errors.day91BillingCode)
                }
              >
                <InputLabel id="day91-label">91 day Billing Code</InputLabel>
                <Select
                  id="active"
                  value={getMappedBillingCode({
                    billingCode: newBillingSchedule.day91BillingCode,
                    isIDTFBilling,
                  })}
                  name="day91BillingCode"
                  label="91 day Billing Code"
                  onChange={(event) => {
                    const value = event.target.value;
                    const copy = cloneDeep(newBillingSchedule);
                    copy.day91BillingCode = value;

                    handleInputChange(copy, originalBillingSchedule);
                    setFieldValue("day91BillingCode", value);
                  }}
                  disabled={!values.day91}
                >
                  {DAY91_BILLING_CODE_OPTIONS.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </Select>
                <ErrorMessage name="day91BillingCode" component="div" />
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                variant="outlined"
                margin="normal"
                label="Custom Days"
                name="customDays"
                value={values.customDays}
                type="number"
                onInput={(event) => {
                  const input = event.target;
                  if (input.value.length > 3) {
                    input.value = input.value.slice(0, 3); // Truncate extra characters
                  }
                }}
                onChange={(event) => {
                  const copy = cloneDeep(newBillingSchedule);
                  const customDaysValue = getNumberInputValue(
                    Number(event.target.value)
                  );
                  copy.custom.days = customDaysValue;

                  handleInputChange(copy, originalBillingSchedule);
                  setFieldValue("customDays", customDaysValue);
                }}
                disabled={!newBillingSchedule?.custom?.enabled}
                error={touched.customDays && Boolean(errors.customDays)}
                helperText={touched.customDays && errors.customDays}
              />

              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                error={
                  touched.customBillingCode && Boolean(errors.customBillingCode)
                }
              >
                <InputLabel id="customBillingCode-label">
                  Custom Billing Code
                </InputLabel>
                <Select
                  id="customBillingCode"
                  label="Custom Billing Code"
                  name="customBillingCode"
                  disabled={!newBillingSchedule.custom.enabled}
                  value={getMappedBillingCode({
                    billingCode: newBillingSchedule.custom.customBillingCode,
                    isIDTFBilling,
                  })}
                  onChange={(event) => {
                    const value = event.target.value;
                    handleCustomBillingCodeSelect(value, newBillingSchedule);
                    setFieldValue("customBillingCode", value);
                  }}
                >
                  {getCustomBillingCodes(isIDTFBilling).map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </Select>
                <ErrorMessage name="customBillingCode" component="div" />
              </FormControl>
            </Grid>
          </Grid>

          <Grid container justifyContent="flex-end"></Grid>

          {isEditMode && (
            <Grid item xs={12}>
              <div className="patient-form__action">
                <Button
                  onClick={() => {
                    setIsEditMode(false);
                    handleInputChange(
                      cloneDeep(originalBillingSchedule),
                      originalBillingSchedule
                    );
                  }}
                  variant="outlined"
                  color="secondary"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={isLoading}
                >
                  Save
                </Button>
              </div>
            </Grid>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default BillingScheduleForm;
